import React, {useState} from 'react';
import Modal from 'shared/components/modal';
import Label from 'shared/components/label';
import FormikTextInput from 'src/components/inputs/formik-text-input';
import FormikTextArea from 'src/components/formik-text-area';
import {Form, Formik} from 'formik';
import {errorMessage, flashMessage} from 'shared/actions/notifications';
import {useDispatch} from 'react-redux';
import ReportIntegrationService, {
  ReportIntegration,
} from '../../services/report-integration-service';
import {ScheduledReport} from '../../services/scheduled-report-service';
import FormikRadioButtons from 'src/components/formik-radio-buttons';
import Checkbox from 'shared/components/checkbox';
import * as Yup from 'yup';
import styles from './report-integration-styles.module.scss';
import vars from 'src/utils/vars';
import Button from '../button';

interface Props {
  onClose: () => void;
  initialValues: Partial<ReportIntegration>;
  updateIntegration: (integration: ReportIntegration) => void;
  universityId: string;
  scheduledReports: ScheduledReport[];
}

const getValidationSchema = (type: 's3' | 'sftp', isEdit: boolean) => {
  const baseSchema = {
    name: Yup.string().required('Name is required'),
    type: Yup.string().oneOf(['s3', 'sftp']).required('Type is required'),
  };

  if (type === 's3') {
    return Yup.object().shape({
      ...baseSchema,
      s3Config: Yup.object().shape({
        bucketName: Yup.string().required('Bucket name is required'),
        prefix: Yup.string().required('Prefix is required'),
        region: Yup.string().required('Region is required'),
        authMethod: Yup.string().oneOf(['bucket_policy', 'credentials']).required(),
        accessKeyId: Yup.string().when('authMethod', {
          is: 'credentials',
          then: (schema) => (isEdit ? schema : schema.required('Access Key ID is required')),
        }),
        secretKey: Yup.string().when('authMethod', {
          is: 'credentials',
          then: (schema) => (isEdit ? schema : schema.required('Secret Key is required')),
        }),
      }),
    });
  } else {
    return Yup.object().shape({
      ...baseSchema,
      sftpConfig: Yup.object().shape({
        host: Yup.string().required('Host is required'),
        port: Yup.number().required('Port is required').positive(),
        path: Yup.string().required('Path is required'),
        username: Yup.string().required('Username is required'),
        authMethod: Yup.string().oneOf(['password', 'ssh_key']).required(),
        password: Yup.string().when('authMethod', {
          is: 'password',
          then: (schema) => (isEdit ? schema : schema.required('Password is required')),
        }),
        sshKey: Yup.string().when('authMethod', {
          is: 'ssh_key',
          then: (schema) => (isEdit ? schema : schema.required('SSH Key is required')),
        }),
      }),
    });
  }
};

const newS3Integration: Partial<ReportIntegration> = {
  name: '',
  type: 's3',
  enabledReports: [],
  s3Config: {
    bucketName: '',
    prefix: '/',
    region: 'us-east-1',
    authMethod: 'bucket_policy',
  },
};

const newSftpIntegration: Partial<ReportIntegration> = {
  name: '',
  type: 'sftp',
  enabledReports: [],
  sftpConfig: {
    host: '',
    port: 22,
    path: '/',
    username: '',
    authMethod: 'ssh_key',
  },
};

export const ReportIntegrationModal = ({
  onClose,
  initialValues,
  universityId,
  updateIntegration,
  scheduledReports,
}: Props) => {
  const dispatch = useDispatch();
  const isEdit = !!initialValues.id;
  const [integrationType, setIntegrationType] = useState<'s3' | 'sftp'>(initialValues.type || 's3');
  const [testError, setTestError] = useState<string | null>(null);

  const handleSubmit = async (values: Partial<ReportIntegration>): Promise<void> => {
    const data = {
      ...values,
      universityId,
    } as ReportIntegration;

    try {
      let integration: ReportIntegration;
      if (isEdit && values.id) {
        integration = await ReportIntegrationService.update(values.id, data, {universityId});
      } else {
        integration = await ReportIntegrationService.create(data);
      }
      updateIntegration(integration);
      dispatch(flashMessage(`Integration has been ${isEdit ? 'updated' : 'created'}`));
      onClose();
    } catch (error) {
      dispatch(errorMessage('Oops! Something went wrong with saving your integration!'));
      console.error(error);
    }
  };

  const getBucketPolicy = (bucketName: string) =>
    JSON.stringify(
      {
        Version: '2012-10-17',
        Statement: [
          {
            Sid: 'AllowExternalAccountPutObject',
            Effect: 'Allow',
            Principal: {
              AWS: `arn:aws:iam::030728503398:role/${
                vars.isStaging ? 'staging' : ''
              }RaiseMeReportIntegrations`,
            },
            Action: ['s3:PutObject', 's3:PutObjectAcl'],
            Resource: `arn:aws:s3:::${bucketName || 'YOUR-BUCKET-NAME'}/*`,
          },
        ],
      },
      null,
      2
    );

  return (
    <Modal show onClose={onClose}>
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={getValidationSchema(integrationType, isEdit)}
        validateOnMount
        enableReinitialize
      >
        {({isSubmitting, isValid, values, setFieldValue}) => {
          const currentType = values.type || 's3';
          const s3Config = values.s3Config || newS3Integration.s3Config!;
          const sftpConfig = values.sftpConfig || newSftpIntegration.sftpConfig!;

          return (
            <Form>
              <Modal.TextHeader onClose={onClose}>
                {isEdit ? 'Edit' : 'Add'} Integration
              </Modal.TextHeader>
              <Modal.Body>
                {testError && (
                  <div className="shared-error-text" role="alert">
                    {testError}
                  </div>
                )}
                <Label required>
                  Name
                  <FormikTextInput name="name" placeholder="Integration Name" />
                </Label>

                <FormikRadioButtons
                  name="type"
                  legendText="Type"
                  required
                  options={[
                    {value: 's3', label: 'S3'},
                    {value: 'sftp', label: 'SFTP'},
                  ]}
                  onChange={(e) => {
                    const newType = e.target.value as 's3' | 'sftp';
                    setIntegrationType(newType);
                    if (newType === 's3') {
                      setFieldValue('s3Config', newS3Integration.s3Config);
                      setFieldValue('sftpConfig', undefined);
                    } else {
                      setFieldValue('sftpConfig', newSftpIntegration.sftpConfig);
                      setFieldValue('s3Config', undefined);
                    }
                  }}
                />

                {currentType === 's3' && (
                  <>
                    <Label required>
                      Bucket Name
                      <FormikTextInput name="s3Config.bucketName" placeholder="my-bucket" />
                    </Label>
                    <Label required>
                      Prefix
                      <FormikTextInput name="s3Config.prefix" placeholder="reports/" />
                    </Label>
                    <Label required>
                      Region
                      <FormikTextInput name="s3Config.region" placeholder="us-east-1" />
                    </Label>

                    <FormikRadioButtons
                      name="s3Config.authMethod"
                      legendText="Auth Method"
                      required
                      options={[
                        {value: 'bucket_policy', label: 'Bucket Policy'},
                        {value: 'credentials', label: 'Credentials'},
                      ]}
                    />

                    {s3Config.authMethod === 'bucket_policy' && (
                      <div className={styles.bucketPolicyContainer}>
                        <div className={styles.bucketPolicyHeader}>
                          <p className="shared-font body bold">Required Bucket Policy:</p>
                          <Button
                            type="button"
                            size="tiny"
                            onClick={() => {
                              navigator.clipboard.writeText(getBucketPolicy(s3Config.bucketName));
                              dispatch(flashMessage('Bucket policy copied to clipboard'));
                            }}
                          >
                            Copy
                          </Button>
                        </div>
                        <pre className={styles.bucketPolicyPre}>
                          {getBucketPolicy(s3Config.bucketName)}
                        </pre>
                      </div>
                    )}

                    {s3Config.authMethod === 'credentials' && (
                      <>
                        <Label required>
                          Access Key ID
                          <FormikTextInput
                            name="s3Config.accessKeyId"
                            placeholder={
                              isEdit ? 'Leave blank to keep existing' : 'AKIAIOSFODNN7EXAMPLE'
                            }
                          />
                        </Label>
                        <Label required={!isEdit}>
                          Secret Key
                          <FormikTextInput
                            name="s3Config.secretKey"
                            type="password"
                            placeholder={isEdit ? 'Leave blank to keep existing' : ''}
                          />
                        </Label>
                      </>
                    )}
                  </>
                )}

                {currentType === 'sftp' && (
                  <>
                    <Label required>
                      Host
                      <FormikTextInput name="sftpConfig.host" placeholder="sftp.example.com" />
                    </Label>
                    <Label required>
                      Port
                      <FormikTextInput name="sftpConfig.port" type="number" placeholder="22" />
                    </Label>
                    <Label required>
                      Path
                      <FormikTextInput name="sftpConfig.path" placeholder="/uploads/" />
                    </Label>
                    <Label required>
                      Username
                      <FormikTextInput name="sftpConfig.username" placeholder="username" />
                    </Label>

                    <FormikRadioButtons
                      name="sftpConfig.authMethod"
                      legendText="Auth Method"
                      required
                      options={[
                        {value: 'ssh_key', label: 'SSH Key'},
                        {value: 'password', label: 'Password'},
                      ]}
                    />

                    {sftpConfig.authMethod === 'password' && (
                      <Label required={!isEdit}>
                        Password
                        <FormikTextInput
                          name="sftpConfig.password"
                          type="password"
                          placeholder={isEdit ? 'Leave blank to keep existing' : ''}
                        />
                      </Label>
                    )}

                    {sftpConfig.authMethod === 'ssh_key' && (
                      <Label required={!isEdit}>
                        SSH Key
                        <FormikTextArea
                          name="sftpConfig.sshKey"
                          placeholder={
                            isEdit
                              ? 'Leave blank to keep existing'
                              : 'Paste your SSH private key here'
                          }
                          rows={6}
                        />
                      </Label>
                    )}
                  </>
                )}

                <div className={styles.enabledReportsSection}>
                  <p className="shared-font body bold">Enabled Reports:</p>
                  {scheduledReports.map((report) => {
                    const isEnabled = values.enabledReports?.some(
                      (enabledReport) =>
                        enabledReport.savedSearchId === report._id &&
                        enabledReport.newFollowersOnly === report.newFollowersOnly
                    );

                    return (
                      <div
                        key={`${report._id}-${report.newFollowersOnly ? '-new-followers' : ''}`}
                        className={styles.reportItem}
                      >
                        <Checkbox
                          checked={isEnabled}
                          onChange={(e) => {
                            const newEnabledReports = e.target.checked
                              ? [
                                  ...(values.enabledReports || []),
                                  {
                                    savedSearchId: report._id,
                                    newFollowersOnly: report.newFollowersOnly,
                                  },
                                ]
                              : (values.enabledReports || []).filter(
                                  (enabledReport) =>
                                    enabledReport.savedSearchId !== report._id ||
                                    enabledReport.newFollowersOnly !== report.newFollowersOnly
                                );
                            setFieldValue('enabledReports', newEnabledReports);
                          }}
                          label={report.filename}
                        />
                      </div>
                    );
                  })}
                </div>
              </Modal.Body>
              <Modal.Footer
                primaryButton={{
                  text: isSubmitting ? 'Saving...' : 'Save',
                  type: 'submit',
                  disabled: isSubmitting || !isValid,
                }}
                secondaryButton={{
                  text: 'Test Integration',
                  disabled: isSubmitting || !isValid,
                  onClick: async () => {
                    try {
                      await ReportIntegrationService.testIntegration(values);
                      dispatch(flashMessage('Test succeeded!'));
                      setTestError(null);
                    } catch (error) {
                      dispatch(flashMessage('Something went wrong!', 'error'));
                      const message = (error as {message?: string})?.message;
                      if (message) {
                        setTestError(message);
                      }
                    }
                  },
                }}
                tertiaryButton={{text: 'Cancel', onClick: onClose}}
              />
            </Form>
          );
        }}
      </Formik>
    </Modal>
  );
};
